/**
 * Componente: Editor de recorte de la foto del contribuyente
 * Paso intermedio entre elegir/capturar la foto y subirla al servidor — deja
 * hacer zoom (slider o rueda del mouse) y arrastrar para reposicionar,
 * siempre con proporción fija 3:4 (retrato), la misma que ya usa el
 * recuadro de foto en la Licencia (LicenseCard, object-fit: cover). Se usa
 * igual desde los dos flujos existentes: "Subir archivo" (PadronFormModal)
 * y "Tomar foto con cámara" (CapturaFotoCamara).
 *
 * Además del encuadre, tiene tres ajustes de color (brillo, contraste e
 * intensidad) con vista previa en vivo; se aplican al JPEG final igual que
 * se ven en pantalla (ver utils/recortarImagen.js).
 *
 * Calidad (utils/calidadFoto.js): si la foto elegida no llega a 300 × 400 píxeles se avisa (sin bloquear: se puede
 * seguir), y el zoom nunca deja el recorte resultante por debajo de 300 × 400 píxeles reales — salvo que la
 * foto original ya sea menor, caso en que no se limita nada y solo queda el aviso.
 *
 * Al confirmar, genera un JPEG ya recortado del tamaño exacto del área
 * elegida y se lo pasa a onConfirmar(blob) — quien lo llama sigue subiéndolo
 * exactamente igual que antes de este cambio. El backend no se entera de
 * nada de este paso: sigue recibiendo un JPEG normal por el mismo endpoint
 * de siempre, sin ningún dato de recorte.
 */

import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  generarImagenRecortada,
  AJUSTES_FOTO_SIN_CAMBIOS,
  hayAjustesDeFoto,
  filtroCssDeAjustes,
} from '../utils/recortarImagen';
import {
  MENSAJE_FOTO_CHICA,
  FOTO_MIN_ANCHO,
  FOTO_MIN_ALTO,
  fotoEsMuyChica,
  zoomMaximoPermitido,
  ZOOM_MAXIMO_EDITOR,
} from '../utils/calidadFoto';
import { IconCerrar, IconExito, IconAdvertencia } from './icons';

const ASPECTO_FOTO_CONTRIBUYENTE = 3 / 4;

const AJUSTES_FOTO = [
  { clave: 'brillo', etiqueta: 'Brillo' },
  { clave: 'contraste', etiqueta: 'Contraste' },
  { clave: 'intensidad', etiqueta: 'Intensidad' },
];

const RecorteFotoModal = ({ imagenSrc, onConfirmar, onCancelar, subiendo = false }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [ajustes, setAjustes] = useState(AJUSTES_FOTO_SIN_CAMBIOS);
  const [areaRecortePx, setAreaRecortePx] = useState(null);
  // Tamaño real de la foto elegida; null hasta que el navegador la termina de cargar.
  const [tamanoFoto, setTamanoFoto] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  // react-easy-crop llama a esto en cada gesto de arrastre/zoom, siempre con
  // el área resultante en píxeles reales de la imagen original (no del
  // recuadro en pantalla) — es lo único que necesitamos guardar.
  const alCompletarRecorte = useCallback((_areaEnPorcentaje, areaEnPixeles) => {
    setAreaRecortePx(areaEnPixeles);
  }, []);

  const alCargarFoto = useCallback((medio) => {
    setTamanoFoto({ ancho: medio.naturalWidth, alto: medio.naturalHeight });
  }, []);

  const fotoChica = !!tamanoFoto && fotoEsMuyChica(tamanoFoto.ancho, tamanoFoto.alto);
  const zoomMaximo = tamanoFoto ? zoomMaximoPermitido(tamanoFoto.ancho, tamanoFoto.alto) : ZOOM_MAXIMO_EDITOR;
  const zoomLimitado = !!tamanoFoto && !fotoChica && zoomMaximo < ZOOM_MAXIMO_EDITOR;
  const sinZoom = zoomMaximo <= 1;

  const ocupado = procesando || subiendo;

  const confirmar = async () => {
    if (!areaRecortePx || ocupado) return;
    try {
      setProcesando(true);
      setError('');
      const blob = await generarImagenRecortada(imagenSrc, areaRecortePx, 0.9, ajustes);
      onConfirmar(blob);
    } catch (err) {
      console.error('Error al generar la imagen recortada:', err);
      setError('No se pudo procesar el recorte. Probá de nuevo.');
      setProcesando(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content recorte-foto-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Ajustar foto</h2>
          <button type="button" className="modal-close" onClick={onCancelar} disabled={ocupado}>
            <IconCerrar size={18} />
          </button>
        </div>

        <div className="modal-body recorte-foto-body">
          {error && (
            <div className="alert alert-error">
              <p>{error}</p>
            </div>
          )}

          {fotoChica && (
            <div className="alert alert-warning" role="alert" data-testid="aviso-foto-chica">
              <IconAdvertencia size={18} />
              <p>{MENSAJE_FOTO_CHICA}</p>
            </div>
          )}

          <div className="recorte-foto-area">
            <Cropper
              image={imagenSrc}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={zoomMaximo}
              aspect={ASPECTO_FOTO_CONTRIBUYENTE}
              cropShape="rect"
              showGrid={false}
              restrictPosition
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={alCompletarRecorte}
              onMediaLoaded={alCargarFoto}
              style={{ mediaStyle: { filter: filtroCssDeAjustes(ajustes) } }}
            />
          </div>

          <div className="recorte-foto-zoom">
            <span className="form-hint">Zoom</span>
            <input
              type="range"
              min={1}
              max={zoomMaximo}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={ocupado || sinZoom}
              aria-label="Zoom de la foto"
            />
          </div>

          {zoomLimitado && (
            <p className="form-hint recorte-foto-limite-zoom">
              {sinZoom
                ? `Esta foto no admite zoom: ya está en el mínimo de ${FOTO_MIN_ANCHO} × ${FOTO_MIN_ALTO} píxeles.`
                : `El zoom llega hasta ×${String(zoomMaximo).replace('.', ',')} para que la foto no quede por debajo de ${FOTO_MIN_ANCHO} × ${FOTO_MIN_ALTO} píxeles.`}
            </p>
          )}

          <div className="recorte-foto-ajustes">
            {AJUSTES_FOTO.map(({ clave, etiqueta }) => (
              <div className="recorte-foto-zoom" key={clave}>
                <span className="form-hint recorte-foto-ajuste-etiqueta">{etiqueta}</span>
                <input
                  type="range"
                  min={50}
                  max={150}
                  step={5}
                  value={ajustes[clave]}
                  onChange={(e) => setAjustes((prev) => ({ ...prev, [clave]: Number(e.target.value) }))}
                  disabled={ocupado}
                  aria-label={`${etiqueta} de la foto`}
                />
                <span className="form-hint recorte-foto-ajuste-valor">{ajustes[clave]}%</span>
              </div>
            ))}
            {hayAjustesDeFoto(ajustes) && (
              <button
                type="button"
                className="btn-accion btn-editar recorte-foto-restablecer"
                onClick={() => setAjustes(AJUSTES_FOTO_SIN_CAMBIOS)}
                disabled={ocupado}
              >
                Restablecer ajustes
              </button>
            )}
          </div>

          <p className="form-hint recorte-foto-ayuda">
            Arrastrá la foto para reposicionarla, y usá el slider (o la rueda del mouse) para acercar o alejar.
          </p>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-cancelar" onClick={onCancelar} disabled={ocupado}>
            Cancelar
          </button>
          <button type="button" className="btn-guardar" onClick={confirmar} disabled={ocupado || !areaRecortePx}>
            {ocupado ? 'Subiendo...' : <><IconExito size={16} /> Usar esta foto</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecorteFotoModal;
